import {
  ApolloClient,
  NormalizedCacheObject,
  QueryOptions,
  OperationVariables,
} from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { BillingChangeRequestType, CaseResolution } from "~/graphql/supergraphEnums";
import { GetFundFamilyCasesDocument } from "~/graphql/supergraphHooks";
import {
  type CaseFilter,
  type CaseSort,
  type GetFundFamilyCasesQuery,
} from "~/graphql/supergraphTypes";
import { PAGINATION } from "~/pages/Billing/constants";
import { formatDate } from "~/pages/Billing/utils";
import { INVALID_RESOLUTION_VALUES, RESOLUTION_FILTER_FIELD } from "./constants";
import { BillingCaseType, type BillingCaseRowItem, type TextFilter } from "./types";

const FIELD_MAP: Record<string, string> = {
  billingCaseId: "billingCaseId",
  clientName: "clientName",
  creationDate: "requestedAt",
  status: "status",
  resolution: "resolution",
  assignee: "assigneeName",
  secondaryAssignee: "secondaryAssigneeName",
};

export const getSortVars = (sortItems: AgGrid.SortModelItem[]): CaseSort[] | null => {
  if (!sortItems) return null;
  const querySorts: CaseSort[] = [];
  for (const item of sortItems) {
    const field = FIELD_MAP[item.colId];
    if (field) {
      querySorts.push({
        field,
        sortAscending: item.sort === "asc",
      });
    }
  }
  return querySorts.length > 0 ? querySorts : null;
};

const expandResolutionFilterValue = (graphqlField: string, value: string): string[] =>
  graphqlField === RESOLUTION_FILTER_FIELD && value === CaseResolution.Invalid
    ? INVALID_RESOLUTION_VALUES
    : [value];

export const getFilterVars = (
  filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null | undefined,
): CaseFilter[] | null => {
  if (!filterModel) {
    return null;
  }
  if (filterModel?.filterType) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }
  const queryFilters: CaseFilter[] = [];
  Object.entries(filterModel ?? {}).forEach((filter) => {
    const [field, filterInfo] = filter;
    const graphqlField = FIELD_MAP[field];
    if (!graphqlField) return;

    if (filterInfo.conditions) {
      if (filterInfo.operator === "OR") {
        queryFilters.push({
          field: graphqlField,
          filterValues: filterInfo.conditions.map((condition: TextFilter) => condition.filter),
        });
      } else if (filterInfo.operator === "AND") {
        filterInfo.conditions.forEach((condition: TextFilter) => {
          queryFilters.push({ field: graphqlField, filterValues: [condition.filter] });
        });
      }
    } else if (filterInfo.filterType === "set") {
      queryFilters.push({
        field: graphqlField,
        filterValues: filterInfo.values.flatMap((value: unknown) =>
          expandResolutionFilterValue(graphqlField, String(value)),
        ),
      });
    } else if (filterInfo.filterType === "text") {
      queryFilters.push({ field: graphqlField, filterValues: [filterInfo.filter] });
    }
  });
  return queryFilters.length > 0 ? queryFilters : null;
};

export type CaseFromQuery = GetFundFamilyCasesQuery["getFundFamilyCases"]["cases"][number];

export const deriveBillingCaseType = (
  requestType: CaseFromQuery["requestType"],
): BillingCaseType => {
  if (requestType === BillingChangeRequestType.FirstCapitalCallDate) {
    return BillingCaseType.FirstCapitalCallDate;
  }
  return BillingCaseType.ClientInformation;
};

export const rowsFromQuery = (
  cases: CaseFromQuery[] | undefined | null,
): BillingCaseRowItem[] => {
  if (!cases) {
    return [];
  }
  return cases.map((caseItem) => ({
    changeRequestId: caseItem.id,
    familyId: caseItem.familyId ?? null,
    clientId: caseItem.clientId,
    billingCaseId: caseItem.billingCaseId,
    type: deriveBillingCaseType(caseItem.requestType),
    requestType: caseItem.requestType ?? null,
    clientName: caseItem.clientName ?? "-",
    creationDate: formatDate(caseItem.requestedAt),
    status: caseItem.status,
    resolution: caseItem.resolution ?? null,
    assignee: caseItem.assigneeName ?? null,
    secondaryAssignee: caseItem.secondaryAssigneeName ?? null,
    assigneeId: caseItem.assigneeId ?? null,
    resolvedByName: caseItem.resolvedByName ?? null,
  }));
};

export class CasesGraphqlDataSource implements AgGrid.IServerSideDatasource {
  client: ApolloClient<NormalizedCacheObject>;
  arenaId: number | null;

  constructor(
    arenaId: number | null = null,
    apolloClient: ApolloClient<NormalizedCacheObject> = client,
  ) {
    this.client = apolloClient;
    this.arenaId = arenaId;
  }

  public handleError(params: AgGrid.IServerSideGetRowsParams): void {
    params.fail();
  }

  public calculateRowCount(
    filteredCount: number | null | undefined,
    rowDataLength: number,
    startRow: number | null | undefined,
    endRow: number | null | undefined,
  ): number {
    if (typeof filteredCount === "number") {
      return filteredCount;
    }

    if (
      startRow === null ||
      startRow === undefined ||
      endRow === null ||
      endRow === undefined
    ) {
      return rowDataLength;
    }

    if (rowDataLength < endRow - startRow) {
      return startRow + rowDataLength;
    }

    return -1;
  }

  public handleSuccess(
    params: AgGrid.IServerSideGetRowsParams,
    response: GetFundFamilyCasesQuery,
  ): void {
    if (!response.getFundFamilyCases) {
      this.handleError(params);
      return;
    }

    const { cases, filteredCount } = response.getFundFamilyCases;
    const rowData = rowsFromQuery(cases);
    const startRow = params.request.startRow ?? 0;
    const endRow = params.request.endRow ?? 0;
    const rowCount = this.calculateRowCount(filteredCount, rowData.length, startRow, endRow);

    params.success({
      rowData,
      rowCount,
    });

    if (rowCount === 0 && startRow === 0) {
      params.api?.showNoRowsOverlay();
    } else if (rowCount > 0) {
      params.api?.hideOverlay();
    }
  }

  public buildQueryOptions(
    params: AgGrid.IServerSideGetRowsParams,
  ): QueryOptions<OperationVariables, GetFundFamilyCasesQuery> {
    const offset = params.request.startRow ?? 0;
    const calculatedLimit = (params.request.endRow ?? 0) - offset;
    const limit = calculatedLimit <= 0 ? PAGINATION.CASES_PAGE_SIZE : calculatedLimit;
    const filters = getFilterVars(params.request.filterModel);
    const sorts = getSortVars(params.request.sortModel);

    return {
      query: GetFundFamilyCasesDocument,
      variables: {
        arenaId: this.arenaId,
        clientId: null,
        decision: null,
        filters,
        sorts,
        offset,
        limit,
      },
      fetchPolicy: "no-cache",
    };
  }

  public executeQuery(
    params: AgGrid.IServerSideGetRowsParams,
    queryOptions: ReturnType<typeof this.buildQueryOptions>,
  ): Promise<void> {
    return this.client
      .query<GetFundFamilyCasesQuery>(queryOptions)
      .then((response) => {
        this.handleSuccess(params, response.data);
      })
      .catch((_error) => {
        this.handleError(params);
      });
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const startRow = params.request.startRow ?? 0;
    if (startRow === 0) {
      params.api?.showLoadingOverlay();
    }
    const queryOptions = this.buildQueryOptions(params);
    void this.executeQuery(params, queryOptions);
  }
}
